/**
 * leakLedgerCopy.ts
 * Curated "Why a leak" / "Action" editorial for the Leak Ledger (Section 4),
 * keyed by funnel step index (`i` from config/activationFunnelSteps.js).
 *
 * This is EDITORIAL copy, not telemetry — it explains a leak and recommends a
 * fix. The Ledger is data-driven (which six leaks surface depends on live
 * counts); a card only renders these boxes when curated copy exists for its
 * step. Keyed by `i` because some steps share an event name (e.g. the
 * first/second-chapter "screen shown" steps both fire ls_first_chapter_screen_shown).
 *
 * Step `i` values are STABLE ids — never renumber (the display order lives in
 * display_index). Keys 20 and 31 belong to the hidden Second Free Chapter
 * steps (SECOND_FREE_CHAPTER_ENABLED = false) and are RETAINED on purpose:
 * they rejoin the page the moment that flag flips.
 */

export interface LeakCopy {
  why: string;
  action: string;
}

export const LEAK_LEDGER_COPY: Record<number, LeakCopy> = {
  // Step 8 · Phase 2 · Create-First-Chapter Shown
  8: {
    why: 'High volume + high drop = the single largest recoverable pool. They saw the screen and didn\'t act — a copy / CTA-visibility problem, not a chapter-engine problem.',
    action: 'A/B the headline, make the single Create button unmissable, and watch Chapter Input Started for recovered Members.',
  },
  // Step 13 · Phase 2 · Chapter Input Started
  13: {
    why: 'The real loss is upstream (only a fraction arrive). Local conversion is healthy — the lever is getting more Members to this step, not fixing the input itself.',
    action: 'Add a starter Legacy Quest prompt + "stop anytime, we\'ll save it" reassurance to recover mid-entry drops.',
  },
  // Step 19 · Phase 2 · First Chapter Created
  19: {
    why: 'The value moment. Members who submitted a draft but have no created chapter are stuck at AI generation or review — the highest-intent group to recover.',
    action: 'Confirm the AI submit → chapter pipeline has no silent failures; surface a clear retry/continue path on the review screen.',
  },
  // Step 20 · Phase 3 · Create-Second-Chapter Shown
  20: {
    why: 'Your instant-gratification-decay question made visible. In-flight Members are a good sign; if they stall, the Ch2 ask needs a fresh quest hook, not a repeat of chapter 1.',
    action: 'Monitor the in-flight Members for the staleness window; if they convert, no fix needed. If they stall, reframe the Ch2 prompt with a new Legacy Quest.',
  },
  // Step 31 · Phase 3 · Second Chapter Created
  31: {
    why: 'Members started a second chapter but didn\'t finish it. Habit is forming but fragile — the second chapter is where the routine either sticks or lapses.',
    action: 'Trigger the Build-the-Habit nurture and a gentle "your story is waiting" reminder for unfinished second chapters.',
  },
  // Step 32 · Phase 4 · Paywall Shown
  32: {
    why: 'Ranks below what its drop suggests because volume is low — recoverable conversion sets priority, not raw drop %. Same surface as the Subscription leak.',
    action: 'Fix alongside Subscription Active — one paywall redesign addresses both leak points at once.',
  },
  // Step 33 · Phase 4 · Trial Started
  33: {
    why: 'Members started a trial but didn\'t convert to an active subscription. Trial-to-paid is where pricing, value framing, and reminder cadence are tested.',
    action: 'Validate the trial-expiry reminder sequence and show the value-stack (chapters + words preserved) before the trial ends.',
  },
  // Step 34 · Phase 4 · Subscription Active
  34: {
    why: 'Highest dollar value of any step. The advance rate is benchmark-normal for trial→paid, so validate any fix against retention — don\'t just chase the rate and pull in low-intent buyers.',
    action: 'Show the value-stack ("chapters, words preserved") before the price; fire the Convert-to-Paid email on the lost group.',
  },
  // Step 48 · Phase 6 · Home Reached
  48: {
    why: 'A retention / habit leak, not an onboarding one. A screen fix won\'t move it — the lever is the lifecycle program.',
    action: 'Treat with the Build-the-Habit nurture flow, not a UI change.',
  },
};
